import React, { useCallback, useMemo } from 'react';

import { InfoHint } from 'components/common/InfoHint';
import { useFormatMode } from 'state/FormatContext';
import type { ActionSettingField } from 'model/actions';
import { formatTypeLabel } from 'model/actions';
import { formatName } from 'model/nameFormatting';
import { getFieldHint, getTypeHint } from 'model/hints';
import { getObjectSchema } from 'model/objectSchemas';
import { toStringValue } from 'utils/strings';

import { ObjectFieldEditor } from './ObjectFieldEditor';
import Styled from './SettingFieldRenderer.styled';

const {
  StyledRow,
  StyledLabel,
  StyledTypeBadge,
  StyledBooleanLabel,
  StyledFullWidthInput,
  StyledSelect,
} = Styled;

interface OwnProps {
  field: ActionSettingField;
  value: unknown;
  /**
   * Stable across renders. Receives the field name + new value so the
   * parent can keep a single `useCallback`-wrapped handler regardless of
   * how many fields are rendered. The renderer wraps this in a locally-
   * stable closure (see `handleChange` below) so `React.memo` on this
   * component is actually effective.
   */
  onSettingChange: (fieldName: string, next: unknown) => void;
}

const SettingFieldRenderer = ({ field, value, onSettingChange }: OwnProps) => {
  const nameMode = useFormatMode();

  const rawValue = value !== undefined ? value : field.defaultValue;

  const typeLabel = formatTypeLabel(field.type, field.isCollection);
  const typeHint = getTypeHint(field.type);
  const fieldHint = getFieldHint(field.name);
  const combinedHint = useMemo(
    () => [fieldHint, typeHint].filter(Boolean).join('\n\n'),
    [fieldHint, typeHint],
  );

  // Behaviour change: the structured-object branch below
  // (`if (objectSchema) { ... <ObjectFieldEditor ... /> }`) was
  // previously unreachable at runtime because the old
  // `NodeSidePanel.tsx` referenced `getObjectSchema` without importing
  // it — that branch threw `ReferenceError` whenever the user opened a
  // settings field whose type matched a registered schema (e.g.
  // `AiSettings`). The Phase 9 split restored the import; this comment
  // preserves the audit trail.
  const objectSchema = useMemo(() => getObjectSchema(field.type), [field.type]);

  // Wrap the parent's stable `onSettingChange` in a locally-stable
  // closure that bakes in `field.name`. Without this, calling the
  // parent's curried function once per field would allocate a fresh
  // closure per render, defeating `React.memo` on this component.
  const handleChange = useCallback(
    (next: unknown) => onSettingChange(field.name, next),
    [onSettingChange, field.name],
  );

  const handleEnumChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => handleChange(event.target.value),
    [handleChange],
  );

  const handleBooleanChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event.target.checked),
    [handleChange],
  );

  const handleNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      handleChange(event.target.value === '' ? undefined : Number(event.target.value)),
    [handleChange],
  );

  const handleStringChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event.target.value),
    [handleChange],
  );

  const handleObjectChange = useCallback(
    (next: Record<string, unknown>) => handleChange(next),
    [handleChange],
  );

  const labelBlock = (
    <StyledLabel htmlFor={`setting-${field.name}`}>
      <span>{formatName(field.name, nameMode)}</span>
      {typeLabel && <StyledTypeBadge title={field.type}>({typeLabel})</StyledTypeBadge>}
      {combinedHint && <InfoHint text={combinedHint} title={field.name} />}
    </StyledLabel>
  );

  if (field.enumValues && field.enumValues.length > 0) {
    const current = toStringValue(rawValue);
    return (
      <StyledRow>
        {labelBlock}
        <StyledSelect id={`setting-${field.name}`} value={current} onChange={handleEnumChange}>
          <option value=''>Select…</option>
          {field.enumValues.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </StyledSelect>
      </StyledRow>
    );
  }

  if (field.type === 'boolean') {
    const current = Boolean(rawValue);
    return (
      <StyledRow>
        <StyledBooleanLabel>
          <input
            id={`setting-${field.name}`}
            type='checkbox'
            checked={current}
            onChange={handleBooleanChange}
          />
          <span>{formatName(field.name, nameMode)}</span>
          {typeLabel && <StyledTypeBadge>({typeLabel})</StyledTypeBadge>}
          {combinedHint && <InfoHint text={combinedHint} title={field.name} />}
        </StyledBooleanLabel>
      </StyledRow>
    );
  }

  if (field.type === 'number') {
    const current = rawValue as number | undefined;
    return (
      <StyledRow>
        {labelBlock}
        <StyledFullWidthInput
          id={`setting-${field.name}`}
          type='number'
          value={current ?? ''}
          onChange={handleNumberChange}
        />
      </StyledRow>
    );
  }

  // Structured object types — render nested editor
  if (objectSchema) {
    return (
      <StyledRow>
        {labelBlock}
        <ObjectFieldEditor
          objectType={field.type}
          value={
            (rawValue && typeof rawValue === 'object' ? rawValue : {}) as Record<string, unknown>
          }
          onChange={handleObjectChange}
          nameMode={nameMode}
        />
      </StyledRow>
    );
  }

  // Generic object fallback — JSON-style editor
  if (rawValue && typeof rawValue === 'object') {
    return (
      <StyledRow>
        {labelBlock}
        <ObjectFieldEditor
          objectType={field.type}
          value={rawValue as Record<string, unknown>}
          onChange={handleObjectChange}
          nameMode={nameMode}
        />
      </StyledRow>
    );
  }

  return (
    <StyledRow>
      {labelBlock}
      <StyledFullWidthInput
        id={`setting-${field.name}`}
        type='text'
        value={toStringValue(rawValue)}
        onChange={handleStringChange}
      />
    </StyledRow>
  );
};

export default React.memo(SettingFieldRenderer);
